import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { Patient, Encounter, ClinicalNotes, Prescription } from '../types';
import { StethoscopeIcon, FlaskConicalIcon, PillIcon, XIcon, RadioIcon, PlusIcon, Trash2Icon, ZoomInIcon, RefreshCwIcon, ChevronDownIcon, ChevronRightIcon, HistoryIcon } from 'lucide-react';
export const ClinicianDashboard: React.FC = () => {
  const {
    patients,
    updateEncounter,
    addNotification
  } = useData();
  const {
    user
  } = useAuth();
  const [selectedEncounter, setSelectedEncounter] = useState<{
    patient: Patient;
    encounter: Encounter;
  } | null>(null);
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [tempInvestigations, setTempInvestigations] = useState<any>(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanType, setScanType] = useState('');
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  // Previous encounters state
  const [expandedPrevEncounters, setExpandedPrevEncounters] = useState<Set<string>>(new Set());
  const togglePrevEncounter = (id: string) => {
    setExpandedPrevEncounters(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  // Live data: get the latest encounter from context so results appear in real-time
  const liveEncounter = selectedEncounter ? (() => {
    const p = patients.find(pt => pt.id === selectedEncounter.patient.id);
    if (!p) return selectedEncounter;
    const enc = p.encounters.find(e => e.id === selectedEncounter.encounter.id);
    if (!enc) return selectedEncounter;
    return {
      patient: p,
      encounter: enc
    };
  })() : null;
  // Clinician queue: With Clinician, Lab Ordered, Radiology Ordered
  const clinicianQueue = patients.flatMap(p => p.encounters.filter(e => ['With Clinician', 'Lab Ordered', 'Radiology Ordered'].includes(e.status)).map(e => ({
    patient: p,
    encounter: e
  })));
  const handleSaveClinicalNotes = (notes: ClinicalNotes) => {
    if (!liveEncounter) return;
    const {
      patient,
      encounter
    } = liveEncounter;
    const updatedEncounter: Encounter = {
      ...encounter,
      clinicalNotes: notes,
      doctorId: user?.id,
      doctorName: user?.name
    };
    updateEncounter(patient.id, updatedEncounter);
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
  };
  const handleOrderInvestigations = (investigations: any) => {
    if (!liveEncounter) return;
    const {
      patient,
      encounter
    } = liveEncounter;
    // Keep status as 'With Clinician' so the file stays open and both lab/radiology can process
    const updatedEncounter: Encounter = {
      ...encounter,
      investigations,
      status: 'With Clinician'
    };
    updateEncounter(patient.id, updatedEncounter);
    if (investigations.ordered.scanUltrasound || investigations.ordered.cxr) {
      addNotification({
        targetRole: 'Radiology',
        message: `Imaging ordered for ${patient.personalDetails.fullNames} (${investigations.scanType || 'General'})`,
        patientId: patient.id
      });
    }
    const hasLabTests = Object.entries(investigations.ordered).some(([k, v]) => v === true && k !== 'scanUltrasound' && k !== 'cxr' && k !== 'other') || investigations.ordered.other && investigations.ordered.other.trim() !== '';
    if (hasLabTests) {
      addNotification({
        targetRole: 'Laboratory',
        message: `Lab tests ordered for ${patient.personalDetails.fullNames}`,
        patientId: patient.id
      });
    }
    // Keep file open — update selectedEncounter with new data
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
    setShowLabModal(false);
  };
  const handleCompleteVisit = () => {
    if (!liveEncounter) return;
    const {
      patient,
      encounter
    } = liveEncounter;
    const today = new Date().toISOString().split('T')[0];
    const futureDates = encounter.followUpDates.filter(d => d >= today);
    if (futureDates.length > 0) {
      addNotification({
        targetRole: 'Reception',
        message: `Review scheduled for ${patient.personalDetails.fullNames} on ${new Date(futureDates[0]).toLocaleDateString()}`,
        patientId: patient.id
      });
    }
    const e = {
      ...encounter,
      status: 'Completed' as const
    };
    updateEncounter(patient.id, e);
    setSelectedEncounter(null);
  };
  const handleRequestScan = () => {
    if (!liveEncounter || !scanType.trim()) return;
    const {
      patient,
      encounter
    } = liveEncounter;
    // Keep status as 'With Clinician' so the file stays open
    const updatedEncounter: Encounter = {
      ...encounter,
      investigations: {
        ...encounter.investigations,
        ordered: {
          ...encounter.investigations.ordered,
          scanUltrasound: true
        },
        scanType: scanType
      },
      status: 'With Clinician'
    };
    updateEncounter(patient.id, updatedEncounter);
    addNotification({
      targetRole: 'Radiology',
      message: `Scan requested for ${patient.personalDetails.fullNames}: ${scanType}`,
      patientId: patient.id
    });
    // Keep file open
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
    setShowScanModal(false);
    setScanType('');
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    if (encounter.prescriptions.length === 0) {
      alert('Please add at least one prescription before sending to Pharmacy.');
      return;
    }
    const updatedEncounter = {
      ...encounter,
      status: 'Pharmacy' as const
    };
    updateEncounter(patient.id, updatedEncounter);
    addNotification({
      targetRole: 'Pharmacy',
      message: `Prescription for ${patient.personalDetails.fullNames}`,
      patientId: patient.id
    });
    // Keep file open so clinician can continue working
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
  };
  const openLabModal = () => {
    if (!selectedEncounter) return;
    setTempInvestigations({
      ...selectedEncounter.encounter.investigations
    });
    setShowLabModal(true);
  };
  const handleTempInvestigationChange = (field: string, value: boolean | string) => {
    setTempInvestigations((prev: any) => ({
      ...prev,
      ordered: {
        ...prev.ordered,
        [field]: value
      }
    }));
  };
  // Prescription Handlers
  const addPrescriptionRow = () => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    const newPrescription: Prescription = {
      id: Date.now().toString(),
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: '',
      signature: user?.name || '',
      dispensed: false
    };
    const updatedEncounter = {
      ...encounter,
      prescriptions: [...encounter.prescriptions, newPrescription]
    };
    updateEncounter(patient.id, updatedEncounter);
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
  };
  const updatePrescription = (id: string, field: keyof Prescription, value: string) => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    const updatedEncounter = {
      ...encounter,
      prescriptions: encounter.prescriptions.map(p => p.id === id ? {
        ...p,
        [field]: value
      } : p)
    };
    updateEncounter(patient.id, updatedEncounter);
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
  };
  const removePrescription = (id: string) => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    const updatedEncounter = {
      ...encounter,
      prescriptions: encounter.prescriptions.filter(p => p.id !== id)
    };
    updateEncounter(patient.id, updatedEncounter);
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
  };
  // Follow-up Dates Handlers
  const handleFollowUpChange = (index: number, value: string) => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    const newDates = [...encounter.followUpDates];
    newDates[index] = value;
    const updatedEncounter = {
      ...encounter,
      followUpDates: newDates.filter(d => d !== '')
    };
    updateEncounter(patient.id, updatedEncounter);
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
  };
  const addFollowUpDate = () => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    if (encounter.followUpDates.length >= 15) return;
    const updatedEncounter = {
      ...encounter,
      followUpDates: [...encounter.followUpDates, '']
    };
    updateEncounter(patient.id, updatedEncounter);
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
  };
  if (selectedEncounter && liveEncounter) {
    const {
      patient,
      encounter
    } = liveEncounter;
    const notes = encounter.clinicalNotes;
    const labResults = encounter.investigations.labResults || {};
    const hasLabResults = Object.keys(labResults).length > 0;
    const hasRadiologyResults = encounter.investigations.radiologyReport || encounter.investigations.radiologyImages.length > 0;
    return <div className="bg-gray-100 min-h-screen pb-12" data-id="element-270">
        {/* Sticky Header Actions */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-4 sticky top-0 z-20 flex justify-between items-center" data-id="element-271">
          <div data-id="element-272">
            <h2 className="text-xl font-bold text-gray-800" data-id="element-273">
              BMC Patient File
            </h2>
            <p className="text-sm text-gray-500" data-id="element-274">
              Attending: {encounter.doctorName || user?.name}
            </p>
          </div>
          <div className="space-x-3" data-id="element-275">
            <button onClick={() => setSelectedEncounter(null)} className="text-gray-600 hover:text-gray-900 font-medium px-4 py-2" data-id="element-276">
              Close File
            </button>
            <button onClick={handleCompleteVisit} className="bg-teal-600 text-white px-6 py-2 rounded-md text-sm font-bold hover:bg-teal-700 shadow-sm" data-id="element-277">
              Complete Visit
            </button>
          </div>
        </div>

        {/* Main Form Container - Paper Style */}
        <div className="max-w-5xl mx-auto mt-6 bg-white shadow-lg border border-gray-300 p-8 sm:p-12" data-id="element-278">
          {/* 1. Header Area */}
          <div className="border-b-2 border-black pb-6 mb-6" data-id="element-279">
            <div className="flex justify-between mb-4" data-id="element-280">
              <div className="text-sm" data-id="element-281">
                <span className="font-bold" data-id="element-282">Date:</span>{' '}
                {new Date(encounter.date).toLocaleDateString()}
              </div>
              <div className="text-sm" data-id="element-283">
                <span className="font-bold" data-id="element-284">File No:</span>{' '}
                {patient.personalDetails.fileNo}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8 text-sm" data-id="element-285">
              <div className="col-span-2 md:col-span-1" data-id="element-286">
                <span className="font-bold" data-id="element-287">1. Full Names:</span>{' '}
                {patient.personalDetails.fullNames}
              </div>
              <div data-id="element-288">
                <span className="font-bold" data-id="element-289">Sex:</span>{' '}
                {patient.personalDetails.sex} &nbsp;&nbsp;&nbsp;{' '}
                <span className="font-bold" data-id="element-290">Age:</span>{' '}
                {patient.personalDetails.age}
              </div>

              <div data-id="element-291">
                <span className="font-bold" data-id="element-292">2. Marital Status:</span>{' '}
                {patient.personalDetails.maritalStatus}
              </div>
              <div className="col-span-2 md:col-span-1" data-id="element-293">
                <span className="font-bold" data-id="element-294">3. Home Address:</span>{' '}
                {patient.personalDetails.homeAddress}
              </div>
              <div data-id="element-295">
                <span className="font-bold" data-id="element-296">Contact:</span>{' '}
                {patient.personalDetails.contact}
              </div>

              <div className="col-span-2" data-id="element-297">
                <span className="font-bold" data-id="element-298">4. Occupation:</span>{' '}
                {patient.personalDetails.occupation}
              </div>

              <div className="col-span-2 flex flex-wrap gap-x-6 gap-y-2" data-id="element-299">
                <span className="font-bold" data-id="element-300">5. Next of Kin:</span>{' '}
                {patient.personalDetails.nextOfKin.name}
                <span data-id="element-301">
                  <span className="font-bold" data-id="element-302">Contact:</span>{' '}
                  {patient.personalDetails.nextOfKin.contact}
                </span>
                <span data-id="element-303">
                  <span className="font-bold" data-id="element-304">Relationship:</span>{' '}
                  {patient.personalDetails.nextOfKin.relationship}
                </span>
              </div>

              <div className="col-span-2" data-id="element-305">
                <span className="font-bold" data-id="element-306">6. Allergies:</span>
                <span className={`ml-2 ${patient.personalDetails.allergies ? 'text-red-600 font-bold' : ''}`} data-id="element-307">
                  {patient.personalDetails.allergies || 'None'}
                </span>
              </div>

              <div className="col-span-2" data-id="element-308">
                <span className="font-bold" data-id="element-309">7. HIV Status:</span>{' '}
                {patient.personalDetails.hivStatus}
              </div>
            </div>
          </div>

          {/* PREVIOUS VISITS HISTORY */}
          {(() => {
          const previousEncounters = patient.encounters.filter(e => e.id !== encounter.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          if (previousEncounters.length === 0) return null;
          return <div className="mb-10 bg-gray-50 border border-gray-300 rounded-lg overflow-hidden" data-id="element-310">
                <div className="bg-gray-200 px-4 py-3 border-b border-gray-300 flex items-center" data-id="element-311">
                  <HistoryIcon className="h-5 w-5 text-gray-600 mr-2" data-id="element-312" />
                  <h3 className="font-bold text-lg text-gray-800 uppercase" data-id="element-313">
                    Previous Visits History
                  </h3>
                  <span className="ml-3 bg-gray-300 text-gray-700 text-xs font-bold px-2 py-1 rounded-full" data-id="element-314">
                    {previousEncounters.length}
                  </span>
                </div>
                <div className="divide-y divide-gray-300" data-id="element-315">
                  {previousEncounters.map(prevEnc => {
                const isExpanded = expandedPrevEncounters.has(prevEnc.id);
                const prevLabResults = prevEnc.investigations?.labResults || {};
                const hasPrevLabResults = Object.keys(prevLabResults).length > 0;
                const hasPrevRadiology = prevEnc.investigations?.radiologyReport || prevEnc.investigations?.radiologyImages && prevEnc.investigations.radiologyImages.length > 0;
                return <div key={prevEnc.id} className="bg-white" data-id="element-316">
                        <button onClick={() => togglePrevEncounter(prevEnc.id)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left" data-id="element-317">
                          <div className="flex items-center space-x-4" data-id="element-318">
                            {isExpanded ? <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-319" /> : <ChevronRightIcon className="h-5 w-5 text-gray-500" data-id="element-320" />}
                            <div data-id="element-321">
                              <span className="font-bold text-teal-700" data-id="element-322">
                                {new Date(prevEnc.date).toLocaleDateString()}
                              </span>
                              <span className="text-gray-500 text-sm ml-3" data-id="element-323">
                                Attending: {prevEnc.doctorName || 'Unknown'}
                              </span>
                            </div>
                          </div>
                          <div className="flex space-x-2" data-id="element-324">
                            {hasPrevLabResults && <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded border border-blue-200" data-id="element-325">
                                Labs
                              </span>}
                            {hasPrevRadiology && <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded border border-purple-200" data-id="element-326">
                                Radiology
                              </span>}
                            {prevEnc.prescriptions?.length > 0 && <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded border border-green-200" data-id="element-327">
                                Rx
                              </span>}
                          </div>
                        </button>

                        {isExpanded && <div className="p-4 bg-gray-50 border-t border-gray-200 text-sm space-y-6" data-id="element-328">
                            {/* Clinical Summary */}
                            <div data-id="element-329">
                              <h4 className="font-bold text-gray-700 border-b border-gray-300 mb-2" data-id="element-330">
                                Clinical Summary
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-331">
                                <div data-id="element-332">
                                  <span className="font-semibold text-gray-600" data-id="element-333">
                                    Presenting Complaint:
                                  </span>
                                  <p className="mt-1 text-gray-800 whitespace-pre-wrap" data-id="element-334">
                                    {prevEnc.clinicalNotes?.presentingComplaint || 'None recorded'}
                                  </p>
                                </div>
                                <div data-id="element-335">
                                  <span className="font-semibold text-gray-600" data-id="element-336">
                                    Working Diagnosis:
                                  </span>
                                  <ul className="mt-1 list-disc list-inside text-gray-800" data-id="element-337">
                                    {prevEnc.clinicalNotes?.workingDiagnosis?.filter(d => d.trim()).length > 0 ? prevEnc.clinicalNotes.workingDiagnosis.filter(d => d.trim()).map((d, i) => <li key={i} data-id="element-338">{d}</li>) : <li data-id="element-339">None recorded</li>}
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Investigations */}
                            {(hasPrevLabResults || hasPrevRadiology) && <div data-id="element-340">
                                <h4 className="font-bold text-gray-700 border-b border-gray-300 mb-2" data-id="element-341">
                                  Investigations
                                </h4>
                                <div className="space-y-4" data-id="element-342">
                                  {hasPrevLabResults && <div className="bg-white p-3 rounded border border-gray-200" data-id="element-343">
                                      <span className="font-semibold text-sm text-gray-600 block mb-2" data-id="element-344">
                                        Laboratory Results:
                                      </span>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-id="element-345">
                                        {Object.entries(prevLabResults).map(([key, value]) => <div key={key} className="bg-gray-50 p-2 rounded border border-gray-100" data-id="element-346">
                                              <div className="text-xs font-bold text-gray-500 uppercase truncate" title={key.replace(/([A-Z])/g, ' $1').trim()} data-id="element-347">
                                                {key.replace(/([A-Z])/g, ' $1').trim()}
                                              </div>
                                              <div className="font-medium text-gray-900" data-id="element-348">
                                                {value as React.ReactNode}
                                              </div>
                                            </div>)}
                                      </div>
                                    </div>}

                                  {hasPrevRadiology && <div className="bg-white p-3 rounded border border-gray-200" data-id="element-349">
                                      <span className="font-semibold text-sm text-gray-600 block mb-2" data-id="element-350">
                                        Radiology:{' '}
                                        {prevEnc.investigations.scanType || 'Scan'}
                                      </span>
                                      {prevEnc.investigations.radiologyReport && <p className="text-gray-800 whitespace-pre-wrap text-sm mb-2 bg-gray-50 p-2 rounded" data-id="element-351">
                                          {prevEnc.investigations.radiologyReport}
                                        </p>}
                                      {prevEnc.investigations.radiologyImages?.length > 0 && <div className="flex gap-2 overflow-x-auto py-2" data-id="element-352">
                                          {prevEnc.investigations.radiologyImages.map((img, idx) => <img key={idx} src={img} alt="Scan" className="h-20 w-20 object-cover border border-gray-300 rounded cursor-pointer hover:border-teal-500 hover:shadow-md transition-all" title="Click to zoom" onClick={e => {
                              e.stopPropagation();
                              setZoomImage(img);
                              setZoomLevel(100);
                            }} data-id="element-353" />)}
                                        </div>}
                                    </div>}
                                </div>
                              </div>}

                            {/* Prescriptions */}
                            {prevEnc.prescriptions?.length > 0 && <div data-id="element-354">
                                <h4 className="font-bold text-gray-700 border-b border-gray-300 mb-2" data-id="element-355">
                                  Prescriptions
                                </h4>
                                <div className="bg-white rounded border border-gray-200 overflow-x-auto" data-id="element-356">
                                  <table className="w-full text-xs text-left" data-id="element-357">
                                    <thead className="bg-gray-50 border-b border-gray-200" data-id="element-358">
                                      <tr data-id="element-359">
                                        <th className="p-2 font-semibold text-gray-600" data-id="element-360">
                                          Drug Name
                                        </th>
                                        <th className="p-2 font-semibold text-gray-600" data-id="element-361">
                                          Dose
                                        </th>
                                        <th className="p-2 font-semibold text-gray-600" data-id="element-362">
                                          Route
                                        </th>
                                        <th className="p-2 font-semibold text-gray-600" data-id="element-363">
                                          Freq
                                        </th>
                                        <th className="p-2 font-semibold text-gray-600" data-id="element-364">
                                          Days
                                        </th>
                                        <th className="p-2 font-semibold text-gray-600" data-id="element-365">
                                          Status
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100" data-id="element-366">
                                      {prevEnc.prescriptions.map((drug, idx) => <tr key={idx} data-id="element-367">
                                            <td className="p-2 font-medium text-gray-900" data-id="element-368">
                                              {drug.drugName}
                                            </td>
                                            <td className="p-2 text-gray-700" data-id="element-369">
                                              {drug.dose}
                                            </td>
                                            <td className="p-2 text-gray-700" data-id="element-370">
                                              {drug.route}
                                            </td>
                                            <td className="p-2 text-gray-700" data-id="element-371">
                                              {drug.frequency}
                                            </td>
                                            <td className="p-2 text-gray-700" data-id="element-372">
                                              {drug.days}
                                            </td>
                                            <td className="p-2" data-id="element-373">
                                              {drug.dispensed ? <span className="text-green-700 font-medium" data-id="element-374">
                                                  Dispensed
                                                </span> : drug.notAvailable ? <span className="text-red-600 font-medium" data-id="element-375">
                                                  Not Avail
                                                </span> : <span className="text-gray-500" data-id="element-376">
                                                  Pending
                                                </span>}
                                            </td>
                                          </tr>)}
                                    </tbody>
                                  </table>
                                </div>
                              </div>}
                          </div>}
                      </div>;
              })}
                </div>
              </div>;
        })()}

          {/* 2. NURSING OBSERVATIONS */}
          <div className="mb-8" data-id="element-377">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-3 uppercase" data-id="element-378">
              Nursing Observations
            </h3>
            <div className="overflow-x-auto" data-id="element-379">
              <table className="w-full text-sm border-collapse border border-gray-300" data-id="element-380">
                <thead data-id="element-381">
                  <tr className="bg-gray-100" data-id="element-382">
                    <th className="border border-gray-300 p-2 w-10 text-center" data-id="element-383">
                      #
                    </th>
                    <th className="border border-gray-300 p-2" data-id="element-384">Temp</th>
                    <th className="border border-gray-300 p-2" data-id="element-385">Pulse</th>
                    <th className="border border-gray-300 p-2" data-id="element-386">Resp</th>
                    <th className="border border-gray-300 p-2" data-id="element-387">BP</th>
                    <th className="border border-gray-300 p-2" data-id="element-388">SpO2</th>
                    <th className="border border-gray-300 p-2" data-id="element-389">Weight (Kg)</th>
                    <th className="border border-gray-300 p-2" data-id="element-390">Height</th>
                    <th className="border border-gray-300 p-2" data-id="element-391">BMI</th>
                    <th className="border border-gray-300 p-2" data-id="element-392">Pain Score</th>
                    <th className="border border-gray-300 p-2" data-id="element-393">AVPU</th>
                  </tr>
                </thead>
                <tbody data-id="element-394">
                  {/* Render up to 10 rows, filling empty ones if needed */}
                  {Array.from({
                  length: Math.max(encounter.vitals.length, 3)
                }).map((_, idx) => {
                  const v = encounter.vitals[idx] || {};
                  return <tr key={idx} data-id="element-395">
                        <td className="border border-gray-300 p-2 text-center font-medium bg-gray-50" data-id="element-396">
                          {idx + 1}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-397">
                          {v.temp || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-398">
                          {v.pulse || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-399">
                          {v.resp || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-400">
                          {v.bp || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-401">
                          {v.spO2 || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-402">
                          {v.weight || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-403">
                          {v.height || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-404">
                          {v.bmi || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-405">
                          {v.painScore || ''}
                        </td>
                        <td className="border border-gray-300 p-2 text-center" data-id="element-406">
                          {v.avpu || ''}
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3. PRESENTING COMPLAINT */}
          <div className="mb-6" data-id="element-407">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-2 uppercase" data-id="element-408">
              Presenting Complaint
            </h3>
            <textarea rows={6} value={notes.presentingComplaint} onChange={e => handleSaveClinicalNotes({
            ...notes,
            presentingComplaint: e.target.value
          })} className="w-full border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 resize-none p-1" placeholder="Enter presenting complaint..." data-id="element-409" />
          </div>

          {/* 4. HISTORY OF PRESENTING COMPLAINT */}
          <div className="mb-8" data-id="element-410">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-2 uppercase" data-id="element-411">
              History of Presenting Complaint
            </h3>
            <textarea rows={4} value={notes.historyOfPresentingComplaint} onChange={e => handleSaveClinicalNotes({
            ...notes,
            historyOfPresentingComplaint: e.target.value
          })} className="w-full border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 resize-none p-1" placeholder="Enter history..." data-id="element-412" />
          </div>

          {/* 5. PAST MEDICAL HISTORY */}
          <div className="mb-8" data-id="element-413">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-3 uppercase" data-id="element-414">
              Past Medical History / General Co-Morbidities
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-4" data-id="element-415">
              {Object.keys(notes.pastMedicalHistory).map(key => <label key={key} className="inline-flex items-center space-x-3" data-id="element-416">
                  <input type="checkbox" checked={notes.pastMedicalHistory[key as keyof typeof notes.pastMedicalHistory]} onChange={e => handleSaveClinicalNotes({
                ...notes,
                pastMedicalHistory: {
                  ...notes.pastMedicalHistory,
                  [key]: e.target.checked
                }
              })} className="form-checkbox h-5 w-5 text-teal-600 rounded border-gray-400 focus:ring-teal-500" data-id="element-417" />
                  <span className="text-sm font-medium uppercase tracking-wider text-gray-700" data-id="element-418">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </label>)}
            </div>
          </div>

          {/* 6. DRUG HISTORY */}
          <div className="mb-8" data-id="element-419">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-2 uppercase" data-id="element-420">
              Drug History
            </h3>
            <textarea rows={3} value={notes.drugHistory} onChange={e => handleSaveClinicalNotes({
            ...notes,
            drugHistory: e.target.value
          })} className="w-full border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 resize-none p-1 mb-4" data-id="element-421" />
            <div className="flex items-center" data-id="element-422">
              <span className="font-bold mr-3" data-id="element-423">Any known allergy:</span>
              <input type="text" value={notes.knownAllergies} onChange={e => handleSaveClinicalNotes({
              ...notes,
              knownAllergies: e.target.value
            })} className="flex-1 border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 p-1" data-id="element-424" />
            </div>
          </div>

          {/* 7. FAMILY MEDICAL HISTORY */}
          <div className="mb-8" data-id="element-425">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-3 uppercase" data-id="element-426">
              Family Medical History
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-4" data-id="element-427">
              {Object.keys(notes.familyMedicalHistory).map(key => <label key={key} className="inline-flex items-center space-x-3" data-id="element-428">
                  <input type="checkbox" checked={notes.familyMedicalHistory[key as keyof typeof notes.familyMedicalHistory]} onChange={e => handleSaveClinicalNotes({
                ...notes,
                familyMedicalHistory: {
                  ...notes.familyMedicalHistory,
                  [key]: e.target.checked
                }
              })} className="form-checkbox h-5 w-5 text-teal-600 rounded border-gray-400 focus:ring-teal-500" data-id="element-429" />
                  <span className="text-sm font-medium uppercase tracking-wider text-gray-700" data-id="element-430">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </label>)}
            </div>
          </div>

          {/* 8. OBSTETRICS AND GYNAECOLOGY HISTORY */}
          <div className="mb-8" data-id="element-431">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-2 uppercase" data-id="element-432">
              Obstetrics and Gynaecology History
            </h3>
            <textarea rows={4} value={notes.obGynHistory} onChange={e => handleSaveClinicalNotes({
            ...notes,
            obGynHistory: e.target.value
          })} className="w-full border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 resize-none p-1" data-id="element-433" />
          </div>

          {/* 9. SOCIAL HISTORY */}
          <div className="mb-8" data-id="element-434">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-3 uppercase" data-id="element-435">
              Social History
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-436">
              {Object.keys(notes.socialHistory).map(key => <div key={key} className="flex items-center" data-id="element-437">
                  <span className="font-medium w-32 capitalize" data-id="element-438">
                    {key.replace(/([A-Z])/g, ' $1').trim()}:
                  </span>
                  <input type="text" value={notes.socialHistory[key as keyof typeof notes.socialHistory]} onChange={e => handleSaveClinicalNotes({
                ...notes,
                socialHistory: {
                  ...notes.socialHistory,
                  [key]: e.target.value
                }
              })} className="flex-1 border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 p-1" data-id="element-439" />
                </div>)}
            </div>
          </div>

          {/* 10. REVIEW OF SYSTEMS */}
          <div className="mb-8" data-id="element-440">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-3 uppercase" data-id="element-441">
              Review of Systems
            </h3>
            <div className="grid grid-cols-1 gap-3" data-id="element-442">
              {Object.keys(notes.reviewOfSystems).map(key => <div key={key} className="flex items-start" data-id="element-443">
                  <span className="font-bold w-16 uppercase" data-id="element-444">{key}:</span>
                  <textarea rows={1} value={notes.reviewOfSystems[key as keyof typeof notes.reviewOfSystems]} onChange={e => handleSaveClinicalNotes({
                ...notes,
                reviewOfSystems: {
                  ...notes.reviewOfSystems,
                  [key]: e.target.value
                }
              })} className="flex-1 border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 resize-none p-0" data-id="element-445" />
                </div>)}
            </div>
          </div>

          {/* 11. ON EXAMINATION */}
          <div className="mb-8" data-id="element-446">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-3 uppercase" data-id="element-447">
              On Examination
            </h3>
            <div className="space-y-4" data-id="element-448">
              <div className="flex flex-wrap items-center gap-4" data-id="element-449">
                <span className="font-bold" data-id="element-450">G/C:</span>
                <select value={notes.onExamination.generalCondition} onChange={e => handleSaveClinicalNotes({
                ...notes,
                onExamination: {
                  ...notes.onExamination,
                  generalCondition: e.target.value as any
                }
              })} className="border-gray-300 rounded-md shadow-sm text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-451">
                  <option value="" data-id="element-452">Select...</option>
                  <option value="Stable" data-id="element-453">Stable</option>
                  <option value="Ill Looking" data-id="element-454">Ill Looking</option>
                  <option value="Unconscious" data-id="element-455">Unconscious</option>
                </select>

                <span className="font-bold ml-4" data-id="element-456">- Fever:</span>
                <select value={notes.onExamination.fever} onChange={e => handleSaveClinicalNotes({
                ...notes,
                onExamination: {
                  ...notes.onExamination,
                  fever: e.target.value as any
                }
              })} className="border-gray-300 rounded-md shadow-sm text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-457">
                  <option value="" data-id="element-458">Select...</option>
                  <option value="Febrile" data-id="element-459">Febrile</option>
                  <option value="Afebrile" data-id="element-460">Afebrile</option>
                  <option value="Lethargic" data-id="element-461">Lethargic</option>
                </select>
              </div>

              <div className="grid grid-cols-1 gap-3" data-id="element-462">
                <div className="flex items-center" data-id="element-463">
                  <span className="font-bold w-20" data-id="element-464">- P|J|C:</span>
                  <input type="text" value={notes.onExamination.pjc} onChange={e => handleSaveClinicalNotes({
                  ...notes,
                  onExamination: {
                    ...notes.onExamination,
                    pjc: e.target.value
                  }
                })} className="flex-1 border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 p-1" data-id="element-465" />
                </div>
                <div className="flex items-center" data-id="element-466">
                  <span className="font-bold w-20" data-id="element-467">S1 S2:</span>
                  <input type="text" value={notes.onExamination.s1s2} onChange={e => handleSaveClinicalNotes({
                  ...notes,
                  onExamination: {
                    ...notes.onExamination,
                    s1s2: e.target.value
                  }
                })} className="flex-1 border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 p-1" data-id="element-468" />
                </div>
                <div className="flex items-center" data-id="element-469">
                  <span className="font-bold w-20" data-id="element-470">R/S:</span>
                  <input type="text" value={notes.onExamination.rs} onChange={e => handleSaveClinicalNotes({
                  ...notes,
                  onExamination: {
                    ...notes.onExamination,
                    rs: e.target.value
                  }
                })} className="flex-1 border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 p-1" data-id="element-471" />
                </div>
                <div className="flex items-start" data-id="element-472">
                  <span className="font-bold w-20 pt-1" data-id="element-473">P/A:</span>
                  <textarea rows={3} value={notes.onExamination.pa} onChange={e => handleSaveClinicalNotes({
                  ...notes,
                  onExamination: {
                    ...notes.onExamination,
                    pa: e.target.value
                  }
                })} className="flex-1 border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 resize-none p-1" data-id="element-474" />
                </div>
              </div>
            </div>
          </div>

          {/* 12. SUMMARY OF SIGNIFICANT FINDINGS */}
          <div className="mb-8" data-id="element-475">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-2 uppercase" data-id="element-476">
              Summary of Significant Findings
            </h3>
            <textarea rows={3} value={notes.summaryOfFindings.join('\n')} onChange={e => handleSaveClinicalNotes({
            ...notes,
            summaryOfFindings: e.target.value.split('\n')
          })} className="w-full border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 resize-none p-1" placeholder="1. ______________________________________________________________________________________" data-id="element-477" />
          </div>

          {/* 13. WORKING DIAGNOSIS */}
          <div className="mb-8" data-id="element-478">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-2 uppercase" data-id="element-479">
              Working Diagnosis
            </h3>
            <textarea rows={3} value={notes.workingDiagnosis.join('\n')} onChange={e => handleSaveClinicalNotes({
            ...notes,
            workingDiagnosis: e.target.value.split('\n')
          })} className="w-full border-0 border-b border-dashed border-gray-400 bg-transparent focus:ring-0 focus:border-teal-500 resize-none p-1" data-id="element-480" />
          </div>

          {/* 14. INVESTIGATION RESULTS (Read-only) */}
          {(hasLabResults || hasRadiologyResults) && <div className="mb-8 bg-gray-50 p-4 border-2 border-gray-300 rounded-md" data-id="element-481">
              <h3 className="font-bold text-lg border-b-2 border-gray-400 mb-4 uppercase" data-id="element-482">
                Investigation Results
              </h3>

              {hasLabResults && <div className="mb-6" data-id="element-483">
                  <h4 className="font-bold text-md mb-2 underline" data-id="element-484">
                    Laboratory Results
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-485">
                    {Object.entries(labResults).map(([key, value]) => <div key={key} className="flex flex-col border-b border-gray-300 pb-1" data-id="element-486">
                        <span className="text-xs font-bold text-gray-600 uppercase" data-id="element-487">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <span className="font-medium" data-id="element-488">{value}</span>
                      </div>)}
                  </div>
                </div>}

              {hasRadiologyResults && <div data-id="element-489">
                  <h4 className="font-bold text-md mb-2 underline" data-id="element-490">
                    Radiology: {encounter.investigations.scanType || 'Scan'}
                  </h4>
                  <div className="bg-white p-3 border border-gray-300 rounded" data-id="element-491">
                    <p className="whitespace-pre-wrap mb-2" data-id="element-492">
                      {encounter.investigations.radiologyReport || 'No report text.'}
                    </p>
                    {encounter.investigations.radiographerName && <p className="text-sm italic text-right" data-id="element-493">
                        - Reported by{' '}
                        {encounter.investigations.radiographerName}
                      </p>}
                    {encounter.investigations.radiologyImages.length > 0 && <div className="mt-4 flex gap-3 overflow-x-auto" data-id="element-494">
                        {encounter.investigations.radiologyImages.map((img, idx) => <img key={idx} src={img} alt="Scan" className="h-28 w-28 object-cover border-2 border-gray-300 rounded cursor-pointer hover:border-teal-500 hover:shadow-md transition-all" title="Click to zoom" onClick={() => {
                  setZoomImage(img);
                  setZoomLevel(100);
                }} data-id="element-495" />)}
                      </div>}
                  </div>
                </div>}
            </div>}

          {/* 15. ACTION BUTTONS */}
          <div className="mb-10 flex gap-4 justify-center" data-id="element-496">
            <button onClick={openLabModal} className="inline-flex items-center px-6 py-3 border-2 border-teal-600 text-sm font-bold rounded text-teal-700 bg-white hover:bg-teal-50" data-id="element-497">
              <FlaskConicalIcon className="mr-2 h-5 w-5" data-id="element-498" />
              Order Investigations
            </button>
            <button onClick={() => setShowScanModal(true)} className="inline-flex items-center px-6 py-3 border-2 border-purple-600 text-sm font-bold rounded text-purple-700 bg-white hover:bg-purple-50" data-id="element-499">
              <RadioIcon className="mr-2 h-5 w-5" data-id="element-500" />
              Request Scan
            </button>
          </div>

          {/* 16. DRUG TREATMENT / PRESCRIPTION */}
          <div className="mb-10" data-id="element-501">
            <div className="flex justify-between items-end border-b border-gray-300 mb-3 pb-2" data-id="element-502">
              <h3 className="font-bold text-lg uppercase" data-id="element-503">
                Drug Treatment / Prescription
              </h3>
              <button onClick={handleSendToPharmacy} className="inline-flex items-center px-4 py-1.5 bg-teal-600 text-white text-sm font-bold rounded hover:bg-teal-700" data-id="element-504">
                <PillIcon className="mr-2 h-4 w-4" data-id="element-505" /> Send to Pharmacy
              </button>
            </div>

            <div className="overflow-x-auto border border-gray-300 rounded-sm" data-id="element-506">
              <table className="w-full text-sm border-collapse" data-id="element-507">
                <thead data-id="element-508">
                  <tr className="bg-gray-100" data-id="element-509">
                    <th className="border border-gray-300 p-2 w-10" data-id="element-510">No.</th>
                    <th className="border border-gray-300 p-2" data-id="element-511">Drug Name</th>
                    <th className="border border-gray-300 p-2 w-24" data-id="element-512">Dose</th>
                    <th className="border border-gray-300 p-2 w-24" data-id="element-513">Route</th>
                    <th className="border border-gray-300 p-2 w-28" data-id="element-514">
                      Frequency
                    </th>
                    <th className="border border-gray-300 p-2 w-24" data-id="element-515">
                      No. of Days
                    </th>
                    <th className="border border-gray-300 p-2 w-32" data-id="element-516">
                      Signature
                    </th>
                    <th className="border border-gray-300 p-2 w-10" data-id="element-517"></th>
                  </tr>
                </thead>
                <tbody data-id="element-518">
                  {encounter.prescriptions.map((p, idx) => <tr key={p.id} data-id="element-519">
                      <td className="border border-gray-300 p-1 text-center bg-gray-50" data-id="element-520">
                        {idx + 1}
                      </td>
                      <td className="border border-gray-300 p-0" data-id="element-521">
                        <input type="text" value={p.drugName} onChange={e => updatePrescription(p.id, 'drugName', e.target.value)} className="w-full border-0 focus:ring-1 focus:ring-teal-500 p-2" data-id="element-522" />
                      </td>
                      <td className="border border-gray-300 p-0" data-id="element-523">
                        <input type="text" value={p.dose} onChange={e => updatePrescription(p.id, 'dose', e.target.value)} className="w-full border-0 focus:ring-1 focus:ring-teal-500 p-2" data-id="element-524" />
                      </td>
                      <td className="border border-gray-300 p-0" data-id="element-525">
                        <input type="text" value={p.route} onChange={e => updatePrescription(p.id, 'route', e.target.value)} className="w-full border-0 focus:ring-1 focus:ring-teal-500 p-2" data-id="element-526" />
                      </td>
                      <td className="border border-gray-300 p-0" data-id="element-527">
                        <input type="text" value={p.frequency} onChange={e => updatePrescription(p.id, 'frequency', e.target.value)} className="w-full border-0 focus:ring-1 focus:ring-teal-500 p-2" data-id="element-528" />
                      </td>
                      <td className="border border-gray-300 p-0" data-id="element-529">
                        <input type="text" value={p.days} onChange={e => updatePrescription(p.id, 'days', e.target.value)} className="w-full border-0 focus:ring-1 focus:ring-teal-500 p-2 text-center" data-id="element-530" />
                      </td>
                      <td className="border border-gray-300 p-0" data-id="element-531">
                        <input type="text" value={p.signature} readOnly className="w-full border-0 bg-gray-50 p-2 text-xs italic" data-id="element-532" />
                      </td>
                      <td className="border border-gray-300 p-1 text-center" data-id="element-533">
                        <button onClick={() => removePrescription(p.id)} className="text-red-500 hover:text-red-700 p-1" data-id="element-534">
                          <Trash2Icon className="h-4 w-4" data-id="element-535" />
                        </button>
                      </td>
                    </tr>)}
                  <tr data-id="element-536">
                    <td colSpan={8} className="border border-gray-300 p-2 text-center bg-gray-50" data-id="element-537">
                      <button onClick={addPrescriptionRow} className="inline-flex items-center text-sm text-teal-600 font-medium hover:text-teal-800" data-id="element-538">
                        <PlusIcon className="h-4 w-4 mr-1" data-id="element-539" /> Add Drug
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 17. NEXT FOLLOW UP / REVIEW DATE */}
          <div data-id="element-540">
            <h3 className="font-bold text-lg border-b border-gray-300 mb-3 uppercase" data-id="element-541">
              Next Follow Up / Review Date (If Any)
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" data-id="element-542">
              {encounter.followUpDates.map((date, idx) => <div key={idx} className="flex border border-gray-300 rounded overflow-hidden" data-id="element-543">
                  <div className="bg-gray-100 px-2 py-1 border-r border-gray-300 font-medium text-sm flex items-center justify-center w-8" data-id="element-544">
                    {idx + 1}
                  </div>
                  <input type="date" value={date} onChange={e => handleFollowUpChange(idx, e.target.value)} className="flex-1 border-0 text-sm p-1 focus:ring-1 focus:ring-teal-500" data-id="element-545" />
                </div>)}
              {encounter.followUpDates.length < 15 && <button onClick={addFollowUpDate} className="border border-dashed border-gray-400 rounded flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-teal-600 text-sm py-1" data-id="element-546">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-547" /> Add Date
                </button>}
            </div>
          </div>
        </div>

        {/* Modals (Scan & Lab) */}
        {showScanModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-548">
            <div className="flex items-center justify-center min-h-screen px-4" data-id="element-549">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowScanModal(false)} data-id="element-550" />
              <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6" data-id="element-551">
                <div className="flex justify-between items-center mb-4 border-b pb-3" data-id="element-552">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-553">
                    Request Radiology Scan
                  </h3>
                  <button onClick={() => setShowScanModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-554">
                    <XIcon className="w-5 h-5" data-id="element-555" />
                  </button>
                </div>
                <div className="space-y-4" data-id="element-556">
                  <div data-id="element-557">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-558">
                      Type of Scan / Investigation
                    </label>
                    <textarea rows={4} value={scanType} onChange={e => setScanType(e.target.value)} placeholder="e.g., Obstetric Ultrasound, Abdominal Ultrasound, Pelvic Scan, Chest X-Ray..." className="w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-purple-500 focus:border-purple-500" data-id="element-559" />
                  </div>
                  <div className="flex justify-end space-x-3" data-id="element-560">
                    <button onClick={() => setShowScanModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-561">
                      Cancel
                    </button>
                    <button onClick={handleRequestScan} disabled={!scanType.trim()} className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-562">
                      Send to Radiology
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {showLabModal && tempInvestigations && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-563">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0" data-id="element-564">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowLabModal(false)} data-id="element-565" />
              <div className="relative inline-block w-full max-w-4xl p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg" data-id="element-566">
                <div className="flex justify-between items-center mb-4 border-b pb-3" data-id="element-567">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-568">
                    TEST REQUESTS
                  </h3>
                  <button onClick={() => setShowLabModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-569">
                    <XIcon className="w-6 h-6" data-id="element-570" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-h-[60vh] overflow-y-auto p-2" data-id="element-571">
                  {/* Column 1 */}
                  <div className="space-y-6" data-id="element-572">
                    <div data-id="element-573">
                      <h4 className="font-semibold text-teal-800 border-b border-teal-100 mb-2 pb-1" data-id="element-574">
                        HAEMATOLOGY
                      </h4>
                      <div className="space-y-2" data-id="element-575">
                        <label className="flex items-center space-x-2 text-sm" data-id="element-576">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.fbc} onChange={e => handleTempInvestigationChange('fbc', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-577" />
                          <span data-id="element-578">Full Blood Count</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-579">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.bloodGroup} onChange={e => handleTempInvestigationChange('bloodGroup', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-580" />
                          <span data-id="element-581">Blood Group</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-582">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.cd4Count} onChange={e => handleTempInvestigationChange('cd4Count', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-583" />
                          <span data-id="element-584">CD4</span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-585">
                      <h4 className="font-semibold text-teal-800 border-b border-teal-100 mb-2 pb-1" data-id="element-586">
                        PARASITOLOGY
                      </h4>
                      <div className="space-y-2" data-id="element-587">
                        <label className="flex items-center space-x-2 text-sm" data-id="element-588">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.urinalysis} onChange={e => handleTempInvestigationChange('urinalysis', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-589" />
                          <span data-id="element-590">Urinalysis</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-591">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.mpsRdt} onChange={e => handleTempInvestigationChange('mpsRdt', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-592" />
                          <span data-id="element-593">Malaria Parasites</span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-594">
                      <h4 className="font-semibold text-teal-800 border-b border-teal-100 mb-2 pb-1" data-id="element-595">
                        MOLECULAR TESTS
                      </h4>
                      <div className="space-y-2" data-id="element-596">
                        <label className="flex items-center space-x-2 text-sm" data-id="element-597">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.viralLoad} onChange={e => handleTempInvestigationChange('viralLoad', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-598" />
                          <span data-id="element-599">HIV Viral Load</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Column 2 */}
                  <div className="space-y-6" data-id="element-600">
                    <div data-id="element-601">
                      <h4 className="font-semibold text-teal-800 border-b border-teal-100 mb-2 pb-1" data-id="element-602">
                        CHEMISTRY
                      </h4>

                      <div className="mt-2 mb-3" data-id="element-603">
                        <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-604">
                          Kidney Function
                        </h5>
                        <div className="space-y-2 pl-2 border-l-2 border-gray-100" data-id="element-605">
                          <label className="flex items-center space-x-2 text-sm" data-id="element-606">
                            <input type="checkbox" checked={!!tempInvestigations.ordered.urea} onChange={e => handleTempInvestigationChange('urea', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-607" />
                            <span data-id="element-608">UREA</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm" data-id="element-609">
                            <input type="checkbox" checked={!!tempInvestigations.ordered.creatinine} onChange={e => handleTempInvestigationChange('creatinine', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-610" />
                            <span data-id="element-611">CREATININE</span>
                          </label>
                        </div>
                      </div>

                      <div className="mt-2" data-id="element-612">
                        <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-613">
                          Glycaemic Profile
                        </h5>
                        <div className="space-y-2 pl-2 border-l-2 border-gray-100" data-id="element-614">
                          <label className="flex items-center space-x-2 text-sm" data-id="element-615">
                            <input type="checkbox" checked={!!tempInvestigations.ordered.fbs} onChange={e => handleTempInvestigationChange('fbs', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-616" />
                            <span data-id="element-617">Fasting Blood Sugar (FBS)</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm" data-id="element-618">
                            <input type="checkbox" checked={!!tempInvestigations.ordered.rbs} onChange={e => handleTempInvestigationChange('rbs', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-619" />
                            <span data-id="element-620">Random Blood Sugar (RBS)</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm" data-id="element-621">
                            <input type="checkbox" checked={!!tempInvestigations.ordered.gtt} onChange={e => handleTempInvestigationChange('gtt', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-622" />
                            <span data-id="element-623">Glucose tolerance Test (GTT)</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm" data-id="element-624">
                            <input type="checkbox" checked={!!tempInvestigations.ordered.postPrandialSugar} onChange={e => handleTempInvestigationChange('postPrandialSugar', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-625" />
                            <span data-id="element-626">2 Hours Post – Prandial Sugar</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Column 3 */}
                  <div className="space-y-6" data-id="element-627">
                    <div data-id="element-628">
                      <h4 className="font-semibold text-teal-800 border-b border-teal-100 mb-2 pb-1" data-id="element-629">
                        SEROLOGY
                      </h4>
                      <div className="space-y-2" data-id="element-630">
                        <label className="flex items-center space-x-2 text-sm" data-id="element-631">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.rpr} onChange={e => handleTempInvestigationChange('rpr', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-632" />
                          <span data-id="element-633">Rapid Plasma Reagin</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-634">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.hiv} onChange={e => handleTempInvestigationChange('hiv', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-635" />
                          <span data-id="element-636">HIV</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-637">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.syphilis} onChange={e => handleTempInvestigationChange('syphilis', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-638" />
                          <span data-id="element-639">Syphilis</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-640">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.genitalSwab} onChange={e => handleTempInvestigationChange('genitalSwab', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-641" />
                          <span data-id="element-642">Genital Swab</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-643">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.gonorrheaAg} onChange={e => handleTempInvestigationChange('gonorrheaAg', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-644" />
                          <span data-id="element-645">Gonorrhea Ag test</span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-646">
                      <h4 className="font-semibold text-teal-800 border-b border-teal-100 mb-2 pb-1" data-id="element-647">
                        HEPATITIS PROFILE
                      </h4>
                      <div className="space-y-2" data-id="element-648">
                        <label className="flex items-center space-x-2 text-sm" data-id="element-649">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.hbsAg} onChange={e => handleTempInvestigationChange('hbsAg', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-650" />
                          <span data-id="element-651">HBs Ag</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-652">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.hbcAg} onChange={e => handleTempInvestigationChange('hbcAg', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-653" />
                          <span data-id="element-654">HBc Ag</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-655">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.hbeAg} onChange={e => handleTempInvestigationChange('hbeAg', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-656" />
                          <span data-id="element-657">HBe Ag</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-658">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.hcv} onChange={e => handleTempInvestigationChange('hcv', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-659" />
                          <span data-id="element-660">HCV</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm" data-id="element-661">
                          <input type="checkbox" checked={!!tempInvestigations.ordered.hav} onChange={e => handleTempInvestigationChange('hav', e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" data-id="element-662" />
                          <span data-id="element-663">HAV</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 border-t pt-4" data-id="element-664">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-665">
                    Others:
                  </label>
                  <textarea rows={2} value={tempInvestigations.ordered.other || ''} onChange={e => handleTempInvestigationChange('other', e.target.value)} className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-teal-500 focus:border-teal-500" placeholder="Specify other tests..." data-id="element-666" />
                </div>

                <div className="mt-6 flex justify-end space-x-3" data-id="element-667">
                  <button onClick={() => setShowLabModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-668">
                    Cancel
                  </button>
                  <button onClick={() => handleOrderInvestigations(tempInvestigations)} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700" data-id="element-669">
                    Send to Laboratory
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={() => setZoomImage(null)} data-id="element-670">
            <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-671">
              <div className="flex items-center gap-3 mb-4 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2" data-id="element-672">
                <button onClick={() => setZoomLevel(z => Math.max(50, z - 25))} className="text-white hover:text-teal-300 font-bold text-lg px-2" title="Zoom out" data-id="element-673">
                  −
                </button>
                <span className="text-white font-medium text-sm min-w-[4rem] text-center" data-id="element-674">
                  {zoomLevel}%
                </span>
                <button onClick={() => setZoomLevel(z => Math.min(200, z + 25))} className="text-white hover:text-teal-300 font-bold text-lg px-2" title="Zoom in" data-id="element-675">
                  +
                </button>
                <button onClick={() => setZoomLevel(100)} className="text-white hover:text-teal-300 ml-2" title="Reset zoom" data-id="element-676">
                  <RefreshCwIcon className="h-4 w-4" data-id="element-677" />
                </button>
                <button onClick={() => setZoomImage(null)} className="text-white hover:text-red-400 ml-4" title="Close" data-id="element-678">
                  <XIcon className="h-5 w-5" data-id="element-679" />
                </button>
              </div>
              <div className="overflow-auto max-w-[85vw] max-h-[80vh] rounded-lg" data-id="element-680">
                <img src={zoomImage} alt="Zoomed scan" style={{
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: 'center center'
            }} className="transition-transform duration-200 ease-out" data-id="element-681" />
              </div>
            </div>
          </div>}
      </div>;
  }
  return <div className="space-y-6" data-id="element-682">
      <h2 className="text-xl font-bold text-gray-800" data-id="element-683">Clinician Queue</h2>

      <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-100" data-id="element-684">
        <ul className="divide-y divide-gray-200" data-id="element-685">
          {clinicianQueue.map(item => <li key={item.encounter.id} data-id="element-686">
              <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 flex items-center justify-between" data-id="element-687">
                <div className="flex items-center" data-id="element-688">
                  <div className="flex-shrink-0" data-id="element-689">
                    <StethoscopeIcon className="h-8 w-8 text-teal-600" data-id="element-690" />
                  </div>
                  <div className="ml-4" data-id="element-691">
                    <p className="text-sm font-medium text-teal-600 truncate" data-id="element-692">
                      {item.patient.personalDetails.fullNames}
                    </p>
                    <div className="flex space-x-2 text-sm text-gray-500" data-id="element-693">
                      <span data-id="element-694">File: {item.patient.personalDetails.fileNo}</span>
                      <span data-id="element-695">•</span>
                      <span className={`font-medium ${item.encounter.status === 'Lab Ordered' ? 'text-orange-500' : item.encounter.status === 'Radiology Ordered' ? 'text-purple-500' : 'text-green-500'}`} data-id="element-696">
                        {item.encounter.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div data-id="element-697">
                  <button onClick={() => setSelectedEncounter(item)} className="inline-flex items-center px-3 py-1 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none" data-id="element-698">
                    Open File
                  </button>
                </div>
              </div>
            </li>)}
          {clinicianQueue.length === 0 && <li className="px-4 py-8 text-center text-gray-500" data-id="element-699">
              No patients waiting for consultation.
            </li>}
        </ul>
      </div>
    </div>;
};